/**
 * 通知タブ (v3.3.2 IA 刷新で追加)
 *
 * 通知関連設定を 1 か所に集約:
 *   - 📧 メール管理: 送信元メールアカウント (旧: メール > 設定 > MailAccountsSection)
 *   - 💬 チャット管理: Google Chat webhook / Chatwork ルーム (旧: 営業・通知設定 > チャット通知。Chatwork は 2026-10-26 追加)
 *   - 🔔 管理者通知: 管理者向け Gmail 通知設定 (旧: 設定 > ② 通知設定)
 *   - 📢 掲示板（全体連絡）: 掲示板ブロードキャストのチャネル (掲示板 Phase 5、owner のみ)
 */
import { Bell, Mail, Megaphone, MessageCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { NotificationsSettingsTab } from './NotificationsSettingsTab';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useMyPermissions } from '@/features/permissions';
import { useNotificationsSettings, useUpdateNotificationsSettings } from '@/hooks/useSettings';
import { MailAccountsSection } from '@/pages/mail/settings/MailAccountsSection';
import { ChatEndpointsTab } from '@/pages/sales-settings/tabs/ChatEndpointsTab';

function SectionHeader({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Mail;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="size-4 text-[var(--accent)]" />
        <h2 className="text-lg font-bold text-[var(--text-primary)]">{title}</h2>
      </div>
      <p className="text-xs text-[var(--text-muted)] leading-relaxed">{description}</p>
    </div>
  );
}

/**
 * 掲示板の「全体連絡」用ブロードキャストチャネル設定 (settings/notifications.boardBroadcast)。
 * 個人別 (Admin.boardNotify) とは別に、全体宛の掲示板通知を送る共通チャネル。owner のみ。
 */
function BoardBroadcastSection() {
  const settings = useNotificationsSettings();
  const update = useUpdateNotificationsSettings();

  const [chatUrl, setChatUrl] = useState('');
  const [chatworkRoom, setChatworkRoom] = useState('');

  useEffect(() => {
    const bb = settings.data?.boardBroadcast;
    setChatUrl(bb?.googleChatWebhookUrl ?? '');
    setChatworkRoom(bb?.chatworkRoomId ?? '');
  }, [settings.data]);

  if (settings.isLoading) {
    return <Spinner />;
  }

  const initial = {
    googleChatWebhookUrl: settings.data?.boardBroadcast?.googleChatWebhookUrl ?? null,
    chatworkRoomId: settings.data?.boardBroadcast?.chatworkRoomId ?? null,
  };
  const current = {
    googleChatWebhookUrl: chatUrl.trim() || null,
    chatworkRoomId: chatworkRoom.trim() || null,
  };
  const isDirty = JSON.stringify(current) !== JSON.stringify(initial);

  const handleSave = async () => {
    try {
      await update.mutateAsync({ boardBroadcast: current });
      toast.success('掲示板の全体連絡チャネルを保存しました');
    } catch (err) {
      toast.error(`保存失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <div className="max-w-lg space-y-4">
      <div className="space-y-1">
        <Label>Google Chat Webhook URL</Label>
        <Input
          value={chatUrl}
          onChange={(e) => setChatUrl(e.target.value)}
          placeholder="https://chat.googleapis.com/v1/spaces/..."
        />
      </div>
      <div className="space-y-1">
        <Label>Chatwork ルーム ID</Label>
        <Input
          value={chatworkRoom}
          onChange={(e) => setChatworkRoom(e.target.value)}
          placeholder="例: 123456789"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="primary"
          onClick={() => void handleSave()}
          disabled={!isDirty || update.isPending}
        >
          {update.isPending ? '保存中…' : '保存'}
        </Button>
        {isDirty && (
          <span className="text-xs text-[var(--accent-amber)]">未保存の変更があります</span>
        )}
      </div>
    </div>
  );
}

export function NotificationsHubTab() {
  const { isOwner } = useMyPermissions();

  return (
    <div className="space-y-10 pb-8">
      <section>
        <SectionHeader
          icon={Mail}
          title="メール管理"
          description="送信元メールアカウント (Gmail OAuth / SMTP) を管理"
        />
        <MailAccountsSection />
      </section>

      <section className="border-t border-[var(--border)] pt-8">
        <SectionHeader
          icon={MessageCircle}
          title="チャット管理"
          description="通知に使う Google Chat と Chatwork の送信先を管理"
        />
        <ChatEndpointsTab />
      </section>

      <section className="border-t border-[var(--border)] pt-8">
        <SectionHeader
          icon={Bell}
          title="管理者通知"
          description="LINE Bot 日次サマリーなど、owner 向けの通知設定"
        />
        <NotificationsSettingsTab />
      </section>

      {isOwner && (
        <section className="border-t border-[var(--border)] pt-8">
          <SectionHeader
            icon={Megaphone}
            title="掲示板（全体連絡）"
            description="掲示板の全体宛通知を送る共通チャネル。個人別の通知設定 (ユーザー詳細) とは別に、全体連絡としてまとめて送信します。"
          />
          <BoardBroadcastSection />
        </section>
      )}
    </div>
  );
}
